import { DatePipe } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, input } from '@angular/core';
import { AdvisorNotification } from '@bp-core/src/lib/proto/advisor-notification.pb';
import { RelativeDaysPipe, calendarDaysBetween } from 'bp/shared/pipes/relative-days.pipe';

/** Hora civil del navegador, 24 h: "15:29". */
const TIME_FORMATTER = new Intl.DateTimeFormat('es-CL', {
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
});

/**
 * Fila de una notificación del asesor: título, cuerpo, antigüedad y punto de "sin leer".
 *
 * Es PRESENTACIONAL — no navega ni marca leído. Quien la usa (el panel de la campana y la página
 * `/notificaciones`) la envuelve en un `<button>` y decide qué hacer con el clic; así la misma fila
 * sirve en los dos lugares sin duplicar estilos.
 *
 * Un tipo desconocido (una notificación de un productor futuro) se muestra tal cual: título y cuerpo
 * vienen resueltos del servidor, la fila no interpreta el catálogo.
 */
@Component({
  selector: 'bp-notification-row',
  standalone: true,
  imports: [DatePipe],
  providers: [RelativeDaysPipe],
  templateUrl: './notification-row.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BpNotificationRowComponent {
  /** El formateador compartido de la 035 resuelve las antigüedades de más de un día. */
  private readonly relativeDays = inject(RelativeDaysPipe);

  readonly notification = input.required<AdvisorNotification>();

  /** Sin `readAt` la notificación está sin leer (contrato del proto). */
  readonly isUnread = computed(() => !this.notification().readAt);

  /** `Timestamp` del proto convertido a fecha del navegador; `null` si el servidor no la mandó. */
  readonly createdAt = computed(() => this.notification().createdAt?.toDate() ?? null);

  /**
   * Antigüedad legible. Hoy y ayer llevan la hora ("Hoy 15:29", "Ayer 15:29") porque en una bandeja
   * casi todo es de hoy y un "Hoy" a secas no distingue lo de recién de lo de la mañana. De dos días
   * en adelante manda el formateador compartido ("2 días", "1.234 días").
   */
  readonly createdAtLabel = computed(() => {
    const created = this.createdAt();
    if (!created) {
      return '';
    }

    const days = calendarDaysBetween(created, new Date());
    if (days <= 0) {
      return `Hoy ${TIME_FORMATTER.format(created)}`;
    }
    if (days === 1) {
      return `Ayer ${TIME_FORMATTER.format(created)}`;
    }

    return this.relativeDays.transform(created);
  });
}
